import { useCallback, useState } from 'react';
import { Group, Stack, createStyles } from '@mantine/core';
import type { FileWithPath } from '@mantine/dropzone';
import { Dropzone } from '@mantine/dropzone';

import Image from '~/domains/common/components/Image/Image';
import type { AssetType } from '~/services/contests/enums/contests-assets.enums';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';

import type { FileRejection } from './types';

export type UploadImageFieldType = BaseInterativeField & {
  type: 'UploadImageField';
  assetType: AssetType;
  action: {
    edit: string;
    remove: string;
  };
  assetConfig: {
    fileSizeLimit: {
      min: number;
      max: number;
    };
    mimeTypes: string[];
  };
};

const useStyles = createStyles((theme) => ({
  root: {
    border: 'none',
    cursor: 'pointer',
    marginBottom: theme.other.spacing._16,
    padding: 0,
    '&:hover': {
      background: 'none',
    },
  },
}));

function UploadImageFieldElement({
  value,
  onValueChange,
  errors,
  field,
  isTouched,
}: StaticFieldProps<UploadImageFieldType>) {
  const { classes, theme } = useStyles();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isLocallyUploaded = value instanceof File;
  const uploadedBlobImage = isLocallyUploaded ? URL.createObjectURL(value) : (value as string);

  const handleDrop = useCallback(
    (files: FileWithPath[]) => {
      const file = files[0];
      onValueChange(file);
      setUploadError(null);
    },
    [onValueChange]
  );

  const onRemoveBanner = useCallback(() => {
    if (isLocallyUploaded) {
      onValueChange(field.defaultValue ?? null);
      setUploadError(null);
    }
  }, [field.defaultValue, isLocallyUploaded, onValueChange]);

  const handleDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      const file = rejectedFiles[0];
      if (file.errors.some((error) => error.code === 'file-too-large')) {
        setUploadError(
          `File is too large. Maximum size allowed is ${field.assetConfig.fileSizeLimit.max / 1024 / 1024} MB.`
        );
      } else if (file.errors.some((error) => error.code === 'file-invalid-type')) {
        setUploadError('File type is not supported.');
      } else {
        setUploadError('File could not be uploaded.');
      }
    },
    [field.assetConfig.fileSizeLimit.max]
  );

  const { fileSizeLimit, mimeTypes } = field.assetConfig;

  return (
    <Stack spacing={0} w="100%">
      <FieldMeta field={field} errors={errors} isTouched={isTouched} />
      <Dropzone
        className={classes.root}
        onDrop={handleDrop}
        onReject={handleDropRejected}
        maxFiles={1}
        maxSize={fileSizeLimit.max}
        accept={mimeTypes}
      >
        <Group position="center" spacing={theme.other.spacing._16}>
          {value ? (
            <Stack>
              <Image
                src={uploadedBlobImage || (field?.defaultValue as string)}
                width={200}
                height={100}
                style={{ objectFit: 'cover' }}
                alt={field.assetType.toLowerCase()}
              />
            </Stack>
          ) : null}
          {!isLocallyUploaded ? (
            <Button variant="tertiary-fill" size="medium" fullWidth>
              {field.action?.edit}
            </Button>
          ) : null}
        </Group>
        {uploadError && (
          <Text variant="label-small" color="orange.3">
            {uploadError}
          </Text>
        )}
      </Dropzone>
      {isLocallyUploaded ? (
        <Button onClick={onRemoveBanner} variant="tertiary-fill" size="medium" fullWidth>
          {field.action?.remove}
        </Button>
      ) : null}
    </Stack>
  );
}

const Descriptor: FieldDescriptor<UploadImageFieldType> = {
  FieldComponent: UploadImageFieldElement,
  name: 'UploadImageField',
};

export default Descriptor;
